import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Cpu, Users, Zap, AlertTriangle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface UsageSummary {
  edge_function: string;
  model: string;
  org_name: string;
  call_count: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  avg_latency_ms: number;
  error_count: number;
}

interface DailyUsage {
  day: string;
  call_count: number;
  total_tokens: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  unique_users: number;
  avg_latency_ms: number;
}

interface UserUsage {
  user_id: string;
  user_name: string;
  org_name: string;
  call_count: number;
  total_tokens: number;
  avg_latency_ms: number;
  last_call: string;
}

export default function AdminAIUsage() {
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<UsageSummary[]>([]);
  const [dailyData, setDailyData] = useState<DailyUsage[]>([]);
  const [userData, setUserData] = useState<UserUsage[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'by-function' | 'by-user'>('overview');

  const fetchData = async () => {
    setLoading(true);
    try {
      const daysNum = parseInt(days);
      const [summaryRes, dailyRes, userRes] = await Promise.all([
        supabase.rpc('get_ai_usage_summary_admin' as any, { _days: daysNum }),
        supabase.rpc('get_ai_usage_by_day_admin' as any, { _days: daysNum }),
        supabase.rpc('get_ai_usage_by_user_admin' as any, { _days: daysNum }),
      ]);

      if (summaryRes.error) throw summaryRes.error;
      if (dailyRes.error) throw dailyRes.error;
      if (userRes.error) throw userRes.error;

      setSummaryData((summaryRes.data as UsageSummary[]) || []);
      setDailyData((dailyRes.data as DailyUsage[]) || []);
      setUserData((userRes.data as UserUsage[]) || []);
    } catch (err) {
      console.error('Failed to fetch AI usage data:', err);
      toast.error('Failed to load AI usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [days]);

  // Aggregate KPIs
  const totalCalls = summaryData.reduce((s, r) => s + r.call_count, 0);
  const totalTokens = summaryData.reduce((s, r) => s + r.total_tokens, 0);
  const totalErrors = summaryData.reduce((s, r) => s + r.error_count, 0);
  const avgLatency = summaryData.length > 0
    ? Math.round(summaryData.reduce((s, r) => s + Number(r.avg_latency_ms) * r.call_count, 0) / Math.max(totalCalls, 1))
    : 0;
  const uniqueUsers = userData.length;
  const tokensPerUser = uniqueUsers > 0 ? Math.round(totalTokens / uniqueUsers) : 0;

  const formatTokens = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  // Chart data (reverse for chronological order)
  const chartData = [...dailyData].reverse().map(d => ({
    day: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    tokens: d.total_tokens,
    calls: d.call_count,
    users: d.unique_users,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Usage</h1>
          <p className="text-muted-foreground">Track LLM token usage across all backend functions</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Zap className="h-4 w-4" /> Total Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-20" /> : (
              <div className="text-2xl font-bold">{totalCalls.toLocaleString()}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Cpu className="h-4 w-4" /> Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-20" /> : (
              <div className="text-2xl font-bold">{formatTokens(totalTokens)}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Cpu className="h-4 w-4" /> Tokens / User
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-20" /> : (
              <div className="text-2xl font-bold">{formatTokens(tokensPerUser)}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" /> Unique Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-20" /> : (
              <div className="text-2xl font-bold">{uniqueUsers}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Avg Latency
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-20" /> : (
              <div className="text-2xl font-bold">{avgLatency}ms</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-20" /> : (
              <div className="text-2xl font-bold text-destructive">{totalErrors}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Token Usage</CardTitle>
          <CardDescription>Total tokens consumed per day</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[250px] w-full" />
          ) : chartData.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No usage data yet. Data will appear after the first AI call with logging.</p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                <XAxis dataKey="day" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tickFormatter={formatTokens} className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                  formatter={(value: number) => [formatTokens(value), 'Tokens']}
                />
                <Bar dataKey="tokens" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {(['overview', 'by-function', 'by-user'] as const).map(tab => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' ? 'By Function & Model' : tab === 'by-function' ? 'By Function' : 'By User'}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <Card>
          <CardHeader>
            <CardTitle>Usage by Function & Model</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Function</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Org</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">Avg Latency</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : summaryData.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No data yet</TableCell></TableRow>
                ) : (
                  summaryData.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">{row.edge_function}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.model}</TableCell>
                      <TableCell className="text-sm">{row.org_name}</TableCell>
                      <TableCell className="text-right font-medium">{row.call_count.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatTokens(row.total_tokens)}</TableCell>
                      <TableCell className="text-right">{Math.round(Number(row.avg_latency_ms))}ms</TableCell>
                      <TableCell className="text-right">
                        {row.error_count > 0 ? (
                          <span className="text-destructive font-medium">{row.error_count}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {activeTab === 'by-function' && (
        <Card>
          <CardHeader>
            <CardTitle>Aggregated by Function</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              // Aggregate by function
              const byFn = summaryData.reduce((acc, row) => {
                const key = row.edge_function;
                if (!acc[key]) acc[key] = { calls: 0, tokens: 0, errors: 0 };
                acc[key].calls += row.call_count;
                acc[key].tokens += row.total_tokens;
                acc[key].errors += row.error_count;
                return acc;
              }, {} as Record<string, { calls: number; tokens: number; errors: number }>);

              const sorted = Object.entries(byFn).sort((a, b) => b[1].tokens - a[1].tokens);

              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Function</TableHead>
                      <TableHead className="text-right">Calls</TableHead>
                      <TableHead className="text-right">Total Tokens</TableHead>
                      <TableHead className="text-right">% of Total</TableHead>
                      <TableHead className="text-right">Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sorted.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No data yet</TableCell></TableRow>
                    ) : sorted.map(([fn, data]) => (
                      <TableRow key={fn}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">{fn}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{data.calls.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{formatTokens(data.tokens)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {totalTokens > 0 ? `${Math.round((data.tokens / totalTokens) * 100)}%` : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {data.errors > 0 ? (
                            <span className="text-destructive font-medium">{data.errors}</span>
                          ) : '0'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {activeTab === 'by-user' && (
        <Card>
          <CardHeader>
            <CardTitle>Usage by User</CardTitle>
            <CardDescription>Top users by token consumption</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Org</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">Avg Latency</TableHead>
                  <TableHead className="text-right">Last Call</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : userData.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No data yet</TableCell></TableRow>
                ) : (
                  userData.map((row) => (
                    <TableRow key={row.user_id}>
                      <TableCell className="font-medium">{row.user_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.org_name}</TableCell>
                      <TableCell className="text-right">{row.call_count.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatTokens(row.total_tokens)}</TableCell>
                      <TableCell className="text-right">{Math.round(Number(row.avg_latency_ms))}ms</TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {new Date(row.last_call).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
